import React from 'react';
import { EntryChild, EntryRow, Mode } from './types';
import { Entity } from '@ecosystem/newron-core/types';
import { elements, actions } from '@ecosystem/newron-design-system';
import { Market } from '../types';

const { Icon } = elements;
const { IconButton, Button } = actions;

type Props = {
  mode: Mode;
  cells: Entity[];
  markets: Market[];
  indicateSubscriptions: boolean;
  onRemoveCells: (cellsToRemove: string[]) => void;
};
function SelectedMarkets({ mode, cells, markets, onRemoveCells, indicateSubscriptions }: Props) {
  const parentCodes = new Set<string>();

  const rows: EntryRow[] = [];
  cells.forEach(cell => {
    const market = markets.find(m => m.value === cell.value);
    const parent: Entity = {
      value: mode === 'byCountry' ? market.countryCode : market.productCode,
      label: mode === 'byCountry' ? market.countryName : market.productName,
    };
    const child: EntryChild = {
      value: mode === 'byCountry' ? market.productCode : market.countryCode,
      label: mode === 'byCountry' ? market.productName : market.countryName,
      subscriptions: market.subscriptions,
    };

    if (!parentCodes.has(parent.value)) {
      rows.push({
        parent,
        children: [child],
      });
      parentCodes.add(parent.value);
    } else {
      const row = rows.find(r => r.parent.value === parent.value);
      if (row) {
        row.children.push(child);
        row.children.sort((a, b) => a.label.localeCompare(b.label));
      }
    }
  });

  rows.sort((a, b) => a.parent.label.localeCompare(b.parent.label));

  const handleRemoveItem = (parentCode: string, childCode: string) => {
    const cell = mode === 'byCountry' ? `${parentCode}-${childCode}` : `${childCode}-${parentCode}`;
    onRemoveCells([cell]);
  };

  const handleRemoveRow = (row: EntryRow) => {
    const cells = row.children.map(child => {
      const cell = mode === 'byCountry' ? `${row.parent.value}-${child.value}` : `${child.value}-${row.parent.value}`;
      return cell;
    });
    onRemoveCells(cells);
  };

  const handleRemoveAll = () => {
    onRemoveCells(cells.map(c => c.value));
  };

  return (
    <div className="relative flex-shrink-0 w-96 flex flex-column border-l border-light">
      <div className="min-h-14 h-14 flex-shrink-0 flex items-center justify-between border-b border-light px-4">
        <span className="flex-grow text-sm text-heading font-bold">Selected markets</span>
        <Button mode="simple" onClick={handleRemoveAll}>
          Clear all
        </Button>
      </div>
      {!cells.length && <div className="text-lighter text-center py-10 px-20">Select at least one market to view</div>}
      {cells.length > 0 && (
        <div className="pb-4 flex-grow overflow-y-auto overscroll-contain bg-brand-lightest">
          {rows.map((row, idx) => (
            <div
              className={`flex flex-row py-2 ${idx !== rows.length - 1 ? 'border-b border-light' : ''}`}
              key={row.parent.value}
            >
              <div className="pl-4 pr-1 py-1 w-28 min-w-28 flex-shrink-0 font-bold text-xs">{row.parent.label}</div>
              <div className="flex-grow flex flex-row gap-x-2 gap-y-1 flex-wrap">
                {row.children.map(child => (
                  <button
                    onClick={() => handleRemoveItem(row.parent.value, child.value)}
                    className="text-left text-xs flex flex-row gap-1 items-center px-2 h-8 rounded transition-colors border border-brand bg-white hover:bg-brand-lightest"
                    key={child.value}
                  >
                    <span className="nowrap overflow-ellipses max-w-40">{child.label}</span>
                    {indicateSubscriptions && <span className="text-lighter">({child.subscriptions})</span>}
                    <Icon size="sm" color="default">
                      <Icon.icons.Close />
                    </Icon>
                  </button>
                ))}
              </div>
              <div className="pr-4 flex-shrink-0">
                <IconButton onClick={() => handleRemoveRow(row)}>
                  <IconButton.icons.Close />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { SelectedMarkets };
